import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { MenuProps, TableColumnsType, TableProps } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import {
  DeleteOutlined,
  LinkOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchOrders } from '../api/ordersApi';
import {
  deleteSupplierOrder,
  fetchSupplierOrder,
  fetchSupplierOrders,
  getSupplierOrderDisplayNumber,
  linkCustomerOrder,
  SupplierOrderDto,
} from '../api/supplierOrdersApi';
import { OrderDto } from '../../../dto/order';
import { getDisplayOrderNumber } from '../../../types/order';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useFeedback } from '../../../utils/feedback';
import { buildCustomerOrderLinkOptions } from '../customerOrderLinkOptions';
import { authService } from '../../../services/auth';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { getSupplierContactsSearchText } from '../../../utils/supplierContacts';
import { formatSupplierOrderQuantity } from '../supplierOrderUnits';
import { buildSupplierOrderLinkPayload } from '../supplierOrderLinkPayload';
import SupplierOrderLinkAllocationFields from './SupplierOrderLinkAllocationFields';
import { buildSupplierOrderAllocationItemOption } from '../supplierOrderAllocationOptions';
import SupplierOrderProductsPreview from '../components/SupplierOrderProductsPreview';
import SupplierOrderSummaryPreview from '../components/SupplierOrderSummaryPreview';

const { Text } = Typography;

const EMPTY_VALUE = '-';

type SupplierOrderRow = SupplierOrderDto;
export type SupplierOrderSortField =
  | 'orderNumber'
  | 'status'
  | 'supplier'
  | 'contact'
  | 'factory'
  | 'products'
  | 'totalAmount'
  | 'totalAmountPLN'
  | 'customerOrders'
  | 'date';

const SUPPLIER_TABLE_SORT_FIELDS: SupplierOrderSortField[] = [
  'orderNumber',
  'status',
  'supplier',
  'contact',
  'factory',
  'products',
  'totalAmount',
  'totalAmountPLN',
  'customerOrders',
  'date',
];

const isSupplierTableSortField = (value: unknown): value is SupplierOrderSortField =>
  typeof value === 'string' && SUPPLIER_TABLE_SORT_FIELDS.includes(value as SupplierOrderSortField);

export interface SupplierOrderFilters {
  status: string[];
  search: string;
  orderNumber: string;
  supplier: string;
  factory: string;
  products: string;
  proformaNumber: string;
  dateRange: [Date | null, Date | null] | null;
  sortBy: SupplierOrderSortField;
  sortOrder: 'asc' | 'desc';
}

interface SupplierOrdersPanelProps {
  filters?: SupplierOrderFilters;
  onSortChange?: (sortBy: SupplierOrderSortField, sortOrder: 'asc' | 'desc') => void;
  onFilteredCountChange?: (count: number) => void;
  showActionBar?: boolean;
}

const SupplierOrdersPanel: React.FC<SupplierOrdersPanelProps> = ({
  filters,
  onSortChange,
  onFilteredCountChange,
  showActionBar = true,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrderRow[]>([]);
  const [customerOrders, setCustomerOrders] = useState<OrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState<'delete' | null>(null);
  const [selectedSupplierOrderIds, setSelectedSupplierOrderIds] = useState<string[]>([]);
  const [selectedSupplierOrder, setSelectedSupplierOrder] = useState<SupplierOrderRow | null>(null);
  const [linkForm] = Form.useForm();
  const selectedCustomerOrderId = Form.useWatch('orderId', linkForm);

  const warehouseWorkflow = Boolean(authService.getCurrentUser()?.invoiceWorkflowEnabled);
  const canViewSupplierOrders = authService.canManageSupplierOrders();
  const canManageSupplierOrders = authService.canManageSupplierOrders();
  const canViewSupplierPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance();

  useEffect(() => {
    if (!canViewSupplierOrders) {
      return;
    }

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewSupplierOrders]);

  const fetchInitialData = async () => {
    await Promise.allSettled([
      fetchSupplierOrdersList(),
      fetchReferenceData(),
    ]);
  };

  const fetchSupplierOrdersList = async () => {
    setLoading(true);
    try {
      const rows = await fetchSupplierOrders();
      setSupplierOrders(rows);
    } catch (error) {
      feedback.error(t('orders:supplierOrders.messages.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    if (warehouseWorkflow) return;
    try {
      const customerOrderRows = await fetchOrders({ isQuote: false }, 'supplierOrders.customerOrders');
      setCustomerOrders(customerOrderRows);
    } catch (error) {
      feedback.error(t('orders:supplierOrders.messages.referenceFetchError'));
    }
  };

  const customerOrderOptions = useMemo(
    () => buildCustomerOrderLinkOptions(customerOrders),
    [customerOrders]
  );
  const selectedSupplierOrderItemOptions = (selectedSupplierOrder?.items || []).map((item) => (
    buildSupplierOrderAllocationItemOption(
      item,
      item.id,
      `${item.name} (${formatSupplierOrderQuantity(item, t, i18n.language)})`,
    )
  ));
  const selectedCustomerOrder = customerOrders.find((order) => order.id === selectedCustomerOrderId) || null;

  const normalizeSearchValue = useCallback(
    (value?: string | number | null) => String(value ?? '').trim().toLocaleLowerCase('pl'),
    []
  );

  const matchesTextFilter = useCallback((value: string, filter?: string) => {
    const normalizedFilter = normalizeSearchValue(filter);
    return !normalizedFilter || normalizeSearchValue(value).includes(normalizedFilter);
  }, [normalizeSearchValue]);

  const getItemSearchValue = useCallback((order: SupplierOrderRow) =>
    order.items
      ?.map((item) => [
        item.name,
        item.description,
        formatProductDimensions(item),
        item.quantity,
        item.unit,
      ].filter(Boolean).join(' '))
      .join(' ') || '', []);

  const getCustomerOrderSearchValue = useCallback((order: SupplierOrderRow) =>
    order.customerOrders
      ?.map((customerOrder) =>
        [
          customerOrder.orderNumber,
          customerOrder.manualOrderNumber,
          customerOrder.customerName,
          canViewSupplierPurchasePrices ? customerOrder.allocatedCostPLN : undefined,
        ].filter(Boolean).join(' ')
      )
      .join(' ') || '', [canViewSupplierPurchasePrices]);

  const getSupplierContactSearchValue = useCallback((order: SupplierOrderRow) =>
    getSupplierContactsSearchText(
      order.supplier?.contactPeople,
      order.supplier?.contactPerson,
      order.supplier?.phone,
      order.supplier?.email
    ), []);

  const getSearchHaystack = useCallback((order: SupplierOrderRow) =>
    [
      order.orderNumber,
      order.manualOrderNumber,
      order.status,
      order.supplier?.name,
      getSupplierContactSearchValue(order),
      order.factory?.name,
      canViewSupplierPurchasePrices ? order.totalAmount : undefined,
      canViewSupplierPurchasePrices ? order.totalAmountPLN : undefined,
      order.supplierReference,
      order.proformaNumber,
      order.orderDate,
      order.expectedDelivery,
      order.notes,
      getItemSearchValue(order),
      getCustomerOrderSearchValue(order),
    ].filter(Boolean).join(' '), [canViewSupplierPurchasePrices, getCustomerOrderSearchValue, getItemSearchValue, getSupplierContactSearchValue]);

  const getEffectiveDate = useCallback((order: SupplierOrderRow) => order.expectedDelivery || order.orderDate || '', []);

  const renderPreviewText = useCallback((value?: string | null, className = 'orders-table__cell-text') =>
    value ? (
      <Tooltip title={value}>
        <span className={className}>{value}</span>
      </Tooltip>
    ) : (
      <Text type="secondary">{EMPTY_VALUE}</Text>
    ), []);

  const renderDocumentReference = useCallback((order: SupplierOrderRow) => {
    if (!order.proformaNumber && !order.supplierReference) {
      return <Text type="secondary">{EMPTY_VALUE}</Text>;
    }

    return (
      <span className="supplier-orders-table__reference">
        {order.proformaNumber && (
          <span className="supplier-orders-table__reference-main">{order.proformaNumber}</span>
        )}
        {order.supplierReference && (
          <span className="supplier-orders-table__reference-secondary">
            {t('orders:supplierOrders.table.supplierReference')}: {order.supplierReference}
          </span>
        )}
      </span>
    );
  }, [t]);

  const renderCostSummary = useCallback((order: SupplierOrderRow) => {
    const hasConvertedValue = order.currency !== 'PLN' && order.totalAmountPLN != null;

    return (
      <span className="supplier-orders-table__cost">
        <span className="supplier-orders-table__cost-main">
          {formatCurrency(order.totalAmount || 0, order.currency)}
        </span>
        {hasConvertedValue && (
          <span className="supplier-orders-table__cost-secondary">
            {formatCurrency(order.totalAmountPLN || 0, 'PLN')}
          </span>
        )}
      </span>
    );
  }, []);

  const getSortValue = useCallback((order: SupplierOrderRow, field: SupplierOrderSortField): string | number => {
    switch (field) {
      case 'orderNumber':
        return getSupplierOrderDisplayNumber(order) || '';
      case 'status':
        return order.status || '';
      case 'supplier':
        return order.supplier?.name || '';
      case 'contact':
        return getSupplierContactSearchValue(order);
      case 'factory':
        return order.factory?.name || '';
      case 'products':
        return getItemSearchValue(order);
      case 'totalAmount':
        return Number(order.totalAmount || 0);
      case 'totalAmountPLN':
        return Number(order.totalAmountPLN || 0);
      case 'customerOrders':
        return Number(order.customerOrderCount || order.customerOrders?.length || 0);
      case 'date': {
        const value = getEffectiveDate(order);
        return value ? new Date(value).getTime() : 0;
      }
      default:
        return '';
    }
  }, [getEffectiveDate, getItemSearchValue, getSupplierContactSearchValue]);

  const displayedSupplierOrders = useMemo(() => {
    if (!filters) {
      return supplierOrders;
    }

    const filteredRows = supplierOrders.filter((order) => {
      const effectiveDate = getEffectiveDate(order);
      const matchesDateRange =
        !filters.dateRange ||
        !filters.dateRange[0] ||
        !filters.dateRange[1] ||
        (effectiveDate &&
          new Date(effectiveDate).getTime() >= filters.dateRange[0].getTime() &&
          new Date(effectiveDate).getTime() <= filters.dateRange[1].getTime());

      return (
        (filters.status.length === 0 || filters.status.includes(order.status || '')) &&
        matchesDateRange &&
        matchesTextFilter(getSearchHaystack(order), filters.search) &&
        matchesTextFilter(
          [
            getSupplierOrderDisplayNumber(order),
            order.orderNumber,
            order.manualOrderNumber,
          ].filter(Boolean).join(' '),
          filters.orderNumber
        ) &&
        matchesTextFilter(order.supplier?.name || '', filters.supplier) &&
        matchesTextFilter(order.factory?.name || '', filters.factory) &&
        matchesTextFilter(getItemSearchValue(order), filters.products) &&
        matchesTextFilter(
          [order.proformaNumber, order.supplierReference].filter(Boolean).join(' '),
          filters.proformaNumber
        )
      );
    });

    return [...filteredRows].sort((left, right) => {
      const direction = filters.sortOrder === 'asc' ? 1 : -1;
      const leftValue = getSortValue(left, filters.sortBy);
      const rightValue = getSortValue(right, filters.sortBy);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }

      return String(leftValue).localeCompare(String(rightValue), 'pl', {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }, [filters, getEffectiveDate, getItemSearchValue, getSearchHaystack, getSortValue, matchesTextFilter, supplierOrders]);

  useEffect(() => {
    onFilteredCountChange?.(displayedSupplierOrders.length);
  }, [displayedSupplierOrders.length, onFilteredCountChange]);

  useEffect(() => {
    setSelectedSupplierOrderIds((current) =>
      current.filter((id) => displayedSupplierOrders.some((order) => order.id === id))
    );
  }, [displayedSupplierOrders]);

  const selectedSupplierOrders = useMemo(
    () => displayedSupplierOrders.filter((order) => selectedSupplierOrderIds.includes(order.id)),
    [displayedSupplierOrders, selectedSupplierOrderIds]
  );

  const clearBulkSelection = () => {
    setSelectedSupplierOrderIds([]);
  };

  const openLinkModal = async (supplierOrder: SupplierOrderRow) => {
    if (!canManageSupplierOrders || warehouseWorkflow) {
      return;
    }

    await fetchReferenceData();
    let orderForLink = supplierOrder;
    try {
      orderForLink = await fetchSupplierOrder(supplierOrder.id);
    } catch (error) {
      feedback.error(t('orders:supplierOrders.messages.fetchError'));
    }

    setSelectedSupplierOrder(orderForLink);
    linkForm.resetFields();
    linkForm.setFieldValue('allocateByItem', false);
    setLinkModalVisible(true);
  };

  const handleLinkCustomerOrder = async (values: any) => {
    if (!selectedSupplierOrder || warehouseWorkflow || !canManageSupplierOrders) return;
    const customerOrder = customerOrders.find((order) => order.id === values.orderId);
    const customerOrderNumber = customerOrder ? getDisplayOrderNumber(customerOrder) : '';
    setSubmitting(true);
    try {
      await linkCustomerOrder(selectedSupplierOrder.id, buildSupplierOrderLinkPayload(values, {
        canEditSupplierOrderFinance,
      }));
      feedback.success(t('orders:supplierOrders.messages.linkSuccess', { customerOrderNumber }));
      setLinkModalVisible(false);
      setSelectedSupplierOrder(null);
      await fetchSupplierOrdersList();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.linkError', undefined, {
        customerOrderNumber,
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async (supplierOrder: SupplierOrderRow) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:supplierOrders.deleteConfirmTitle'),
        content: getSupplierOrderDisplayNumber(supplierOrder),
        okText: t('common:delete'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    try {
      const deletedNumber = getSupplierOrderDisplayNumber(supplierOrder);
      await deleteSupplierOrder(supplierOrder.id);
      feedback.success(t('orders:supplierOrders.messages.deleteSuccess', {
        orderNumber: deletedNumber,
      }));
      await fetchSupplierOrdersList();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.messages.deleteError', undefined, {
        orderNumber: getSupplierOrderDisplayNumber(supplierOrder),
      }));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSupplierOrders.length === 0) {
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:supplierOrders.bulk.deleteConfirmTitle', { count: selectedSupplierOrders.length }),
        content: t('orders:supplierOrders.bulk.deleteConfirmDescription'),
        okText: t('orders:supplierOrders.bulk.delete'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    setBulkActionLoading('delete');

    try {
      for (const order of selectedSupplierOrders) {
        await deleteSupplierOrder(order.id);
      }

      feedback.success(t('orders:supplierOrders.bulk.deleteSuccess', { count: selectedSupplierOrders.length }));
      clearBulkSelection();
      await fetchSupplierOrdersList();
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'orders:supplierOrders.bulk.deleteError'));
    } finally {
      setBulkActionLoading(null);
    }
  };

  const getRowMenuItems = (record: SupplierOrderRow): MenuProps['items'] => [
    ...(!warehouseWorkflow ? [{
      key: 'link',
      icon: <LinkOutlined />,
      label: t('orders:supplierOrders.linkCustomerOrder'),
      disabled: !canManageSupplierOrders,
    }] : []),
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('common:delete'),
      danger: true,
      disabled: !canManageSupplierOrders,
    },
  ];

  const getColumnSortProps = (field: SupplierOrderSortField): { sorter: true; sortOrder: SortOrder } => ({
    sorter: true,
    sortOrder: filters?.sortBy === field ? (filters.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
  });

  const handleTableChange: TableProps<SupplierOrderRow>['onChange'] = (_pagination, _tableFilters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter.find((item) => item.order) : sorter;
    const columnKey = activeSorter?.columnKey;

    if (!activeSorter?.order || !isSupplierTableSortField(columnKey)) {
      return;
    }

    onSortChange?.(columnKey, activeSorter.order === 'ascend' ? 'asc' : 'desc');
  };

  const rowSelection: TableProps<SupplierOrderRow>['rowSelection'] = canManageSupplierOrders
    ? {
        selectedRowKeys: selectedSupplierOrderIds,
        onChange: (keys: React.Key[]) => setSelectedSupplierOrderIds(keys.map(String)),
        preserveSelectedRowKeys: false,
        columnWidth: 54,
      }
    : undefined;

  const columns: TableColumnsType<SupplierOrderRow> = [
    {
      title: t('orders:supplierOrders.table.supplier'),
      key: 'supplier',
      ...getColumnSortProps('supplier'),
      width: 240,
      render: (_, record) => (
        <SupplierOrderSummaryPreview
          order={record}
          onOrderClick={() => navigate(`/app/supplier-orders/${record.id}`)}
        />
      ),
    },
    {
      title: t('orders:supplierOrders.table.products'),
      key: 'products',
      ...getColumnSortProps('products'),
      width: 300,
      render: (_, record) => <SupplierOrderProductsPreview items={record.items} />,
    },
    {
      title: t('orders:supplierOrders.table.factory'),
      dataIndex: ['factory', 'name'],
      key: 'factory',
      ...getColumnSortProps('factory'),
      width: 150,
      render: (value?: string | null) => renderPreviewText(value),
    },
    {
      title: t('orders:supplierOrders.table.expectedDelivery'),
      dataIndex: 'expectedDelivery',
      key: 'date',
      ...getColumnSortProps('date'),
      width: 120,
      render: (value?: string | null) => value ? formatDate(value) : <Text type="secondary">-</Text>,
    },
    {
      title: t('orders:supplierOrders.table.documentReference'),
      key: 'documentReference',
      width: 160,
      render: (_, record) => renderDocumentReference(record),
    },
    ...(canViewSupplierPurchasePrices
      ? [{
          title: t('orders:supplierOrders.table.totalAmount'),
          key: 'totalAmount',
          ...getColumnSortProps('totalAmount'),
          width: 140,
          align: 'right' as const,
          render: (_: unknown, record: SupplierOrderRow) => renderCostSummary(record),
        }]
      : []),
  ];

  columns.unshift({
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'link') void openLinkModal(record);
              if (key === 'delete') void confirmDelete(record);
            },
          }}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      ),
  });

  const renderActionBar = () => (
    <div className="orders-page__workspace-bar">
      <div className="orders-page__quick-actions orders-page__quick-actions--left">
        <span className="orders-page__count">
          {t('orders:resultsCount', { count: supplierOrders.length })}
        </span>
      </div>
      <div className="orders-page__quick-actions">
        {canManageSupplierOrders && (
          <Button className="orders-page__create-button" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/app/supplier-orders/new')}>
            {t('orders:supplierOrders.newSupplierOrder')}
          </Button>
        )}
      </div>
    </div>
  );

  const renderBulkToolbar = () => (
    <div className="app-page__bulkbar">
      <div className="app-page__bulkbar-copy">
        <span className="app-page__bulkbar-label">{t('orders:supplierOrders.bulk.label')}</span>
        <span className="app-page__bulkbar-count">
          {selectedSupplierOrderIds.length > 0
            ? t('orders:supplierOrders.bulk.selectedCount', { count: selectedSupplierOrders.length })
            : t('orders:supplierOrders.bulk.empty')}
        </span>
      </div>

      <div className="app-page__bulkbar-actions">
        <Button
          danger
          onClick={handleBulkDelete}
          disabled={selectedSupplierOrders.length === 0}
          loading={bulkActionLoading === 'delete'}
          icon={<DeleteOutlined />}
        >
          {t('orders:supplierOrders.bulk.delete')}
        </Button>
        <Button onClick={clearBulkSelection} disabled={selectedSupplierOrderIds.length === 0}>
          {t('orders:supplierOrders.bulk.clearSelection')}
        </Button>
      </div>
    </div>
  );

  if (!canViewSupplierOrders) {
    return null;
  }

  return (
    <div className="supplier-orders-panel">
      {showActionBar ? renderActionBar() : null}
      {canManageSupplierOrders && selectedSupplierOrderIds.length > 0 ? renderBulkToolbar() : null}

      <Table
        className="orders-table orders-table--summary"
        rowKey="id"
        columns={columns}
        dataSource={displayedSupplierOrders}
        rowSelection={rowSelection}
        loading={loading}
        onChange={handleTableChange}
        sortDirections={['ascend', 'descend', 'ascend']}
        scroll={{ x: 1180 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title={t('orders:supplierOrders.linkCustomerOrder')}
        open={!warehouseWorkflow && linkModalVisible}
        onCancel={() => setLinkModalVisible(false)}
        footer={null}
      >
        <Form form={linkForm} layout="vertical" onFinish={handleLinkCustomerOrder}>
          <Form.Item name="orderId" label={t('orders:supplierOrders.form.customerOrder')} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={customerOrderOptions} />
          </Form.Item>
          <SupplierOrderLinkAllocationFields
            form={linkForm}
            canEditSupplierOrderFinance={canEditSupplierOrderFinance}
            supplierOrderItemOptions={selectedSupplierOrderItemOptions}
            canonicalItemAllocations
            customerOrder={selectedCustomerOrder}
            supplierOrderItems={selectedSupplierOrder?.items || []}
          />
          <Form.Item name="notes" label={t('orders:notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>{t('common:save')}</Button>
              <Button onClick={() => setLinkModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierOrdersPanel;
